import React, { useContext } from 'react';
import I18n from 'i18n-js';
import {
  core_apimessages_Money,
} from '@reverbdotcom/commons/src/gql/graphql';
import greatValueIcon from '@reverbdotcom/cadence/images/nudge/gem.svg';
import exclamationTriangleIcon from '@reverbdotcom/cadence/icons/svgs/exclamation-triangle.svg';
import exclamationCircleIcon from '@reverbdotcom/cadence/icons/svgs/exclamation-circle.svg';
import classNames from 'classnames';
import { SellFormContext } from '../SellFormContext';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { conditionUuidForSlug } from '@reverbdotcom/commons/src/condition_helpers';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { gql } from '@reverbdotcom/commons/src/gql';


const LIKELY_TO_SELL_SCORE = 0.5;
const UNLIKELY_TO_SELL_SCORE = 1.2;
const MINIMUM_PRICE_CENTS = 100;
const COMPONENT_NAME = 'SellFormPriceSignalCallout';

interface IExternalProps {
  calloutPrice: core_apimessages_Money;
  conditionSlug: string;
}

function getPriceCalloutData(
  priceCondition: string,
  estimatedNewListingPrice: core_apimessages_Money,
) {
  const calloutDataMap = {
    priceHigherThanNewListingPrice: {
      icon: {
        src: exclamationTriangleIcon,
        alt: I18n.t('discovery.sellForm.pricingSection.priceSignals.higherThanNewPrice.iconAlt'),
      },
      text: I18n.t('discovery.sellForm.pricingSection.priceSignals.higherThanNewPrice.text', {
        newPrice: estimatedNewListingPrice?.display,
      }),
      calloutClass: 'warning',
    },
    likelyToSell: {
      title: I18n.t('discovery.sellForm.pricingSection.priceSignals.likelyToSell.title'),
      icon: {
        src: greatValueIcon,
        alt: I18n.t('discovery.sellForm.pricingSection.priceSignals.likelyToSell.greatValueIconAlt'),
      },
      text: I18n.t('discovery.sellForm.pricingSection.priceSignals.likelyToSell.text'),
      subtext: I18n.t('discovery.sellForm.pricingSection.priceSignals.likelyToSell.subText'),
      calloutClass: 'success',
    },
    lessLikelyToSell: {
      icon: {
        src: exclamationCircleIcon,
        alt: I18n.t('discovery.sellForm.pricingSection.priceSignals.lessLikelyToSell.iconAlt'),
      },
      text: I18n.t('discovery.sellForm.pricingSection.priceSignals.lessLikelyToSell.text'),
      calloutClass: 'info',
    },
    unlikelyToSell: {
      icon: {
        src: exclamationTriangleIcon,
        alt: I18n.t('discovery.sellForm.pricingSection.priceSignals.unlikelyToSell.iconAlt'),
      },
      text: I18n.t('discovery.sellForm.pricingSection.priceSignals.unlikelyToSell.text'),
      calloutClass: 'warning',
    },
  };

  return calloutDataMap[priceCondition];
}


function findPriceCallout({ calloutPrice, estimatedNewListingPrice, priceScore }) {
  const priceHigherThanNewListingPrice = calloutPrice?.amountCents > estimatedNewListingPrice?.amountCents;
  const likelyToSell = priceScore <= LIKELY_TO_SELL_SCORE;
  const lessLikelyToSell = priceScore > LIKELY_TO_SELL_SCORE;
  const unlikelyToSell = priceScore >= UNLIKELY_TO_SELL_SCORE;

  const priceCallouts = [
    { condition: 'priceHigherThanNewListingPrice', active: priceHigherThanNewListingPrice },
    { condition: 'unlikelyToSell', active: unlikelyToSell },
    { condition: 'lessLikelyToSell', active: lessLikelyToSell },
    { condition: 'likelyToSell', active: likelyToSell },
  ];
  return priceCallouts.find(c => c.active)?.condition ?? null;
}

export default function SellFormPriceSignalCallout({
  calloutPrice,
  conditionSlug,
}: IExternalProps) {
  const { currentCanonicalProductId: canonicalProductId } = useContext(SellFormContext);
  const conditionUuid = conditionUuidForSlug(conditionSlug);
  const {
    data: newPriceData,
  } = useQuery(
    ESTIMATED_NEW_LISTING_PRICE_QUERY,
    {
      variables: { canonicalProductId },
    },
  );
  const {
    data: priceScoresData,
  } = useQuery(
    PRICE_SCORE_QUERY,
    {
      skip: !calloutPrice,
      variables: {
        canonicalProductId,
        conditionUuid: conditionUuid,
        price: calloutPrice,
        countryCode: 'US',
      },
    },
  );

  const priceHigherThanMinimumPrice = calloutPrice?.amountCents > MINIMUM_PRICE_CENTS;
  const estimatedNewListingPrice = newPriceData?.canonicalproduct?.estimatedNewListingPrice?.price;
  const priceScore = priceScoresData?.priceScores?.priceScores[0]?.priceScore;

  const activePriceCallout = findPriceCallout({
    calloutPrice,
    estimatedNewListingPrice,
    priceScore,
  });

  const calloutData = getPriceCalloutData(activePriceCallout, estimatedNewListingPrice);

  const visible = calloutData && priceHigherThanMinimumPrice;

  const calloutClasses = classNames(
    'sell-form-great-value-callout', {
      'sell-form-great-value-callout--visible': visible,
    },
  );

  const contentClassNames = classNames(
    'sell-form-price-signal-callout__content',
    calloutData?.calloutClass,
  );

  if (visible) {
    trackEvent({
      eventName: MParticleEventName.ComponentView,
      componentName: COMPONENT_NAME,
      currentView: activePriceCallout,
    });
  }

  return (
    <div className={calloutClasses}>
      {calloutData &&
        <div className={contentClassNames}>
          <div className="sell-form-price-signal-callout__title">{calloutData.title}</div>
          <div className="sell-form-price-signal-callout__body">
            <div className="sell-form-price-signal-callout__body__icon">
              <img alt={calloutData.icon.alt} src={calloutData.icon.src} />
            </div>
            <div className="sell-form-price-signal-callout__body-content">
              <div className="sell-form-price-signal-callout__body__title">{calloutData.text}</div>
              <div className="sell-form-price-signal-callout__body__text">{calloutData.subtext}</div>
            </div>
          </div>
        </div>
      }
    </div>
  );
}

export const ESTIMATED_NEW_LISTING_PRICE_QUERY = gql(`
  query Core_SellForm_EstimatedNewListingPrice($canonicalProductId: String!) {
    canonicalproduct(input: { id: $canonicalProductId }) {
      _id
      estimatedNewListingPrice {
        price {
          amountCents
          display
        }
      }
    }
  }
`);


export const PRICE_SCORE_QUERY = gql(`
  query DataServices_SellForm_PriceScore($canonicalProductId: String!, $conditionUuid: String!, $price: Input_reverb_pricing_Money!, $countryCode: String!){
    priceScores(input: { priceScoreQueries: [{
      canonicalProductId: $canonicalProductId,
      conditionUuid: $conditionUuid,
      price: $price,
      countryCode: $countryCode
    }] }) {
      priceScores {
        priceScore 
      }
    }
  }
 `);
